import { useEffect, useMemo, useRef, useState } from 'react';
import { c, card, tbl } from '../../theme';
import { fmtCount, fmtHm, fmtClock, fmtPct } from './format';
import {
  INK, TONE, adherenceTone, avatarColor, channelDot, efficiencyTone, initials, podTone, scoreTone,
} from './badges';

// Every agent's figures, one block of columns per period. Click a header to
// sort by it (again to reverse); click a row to open the agent.
//
// The columns, their order and the gear that hides them are the old app's:
// Agent and Pod are always shown, and the eleven that follow can be turned off
// one at a time, remembered in this browser under the same key the old app
// used. Anything the period has no work for shows a dash, not a zero: there
// is no handle time to average.
//
// Each period is an object { key, label, byUser: Map(userId -> figures) }.

export const transferPct = (a) => (a && a.handled > 0 ? Math.round((a.transferred / a.handled) * 1000) / 10 : null);

// One block per period, in the old app's order — Hand/Hr sits after Hold, and
// Adher before Eff%, which is not where the portal had put them.
const PERIOD_COLUMNS = [
  { key: 'channels', label: 'Channel', type: 'channels', align: 'left', title: 'Channels the agent handled in this period' },
  { key: 'handled', label: 'Hand', type: 'count', title: 'Distinct conversations: inbound calls, callbacks that connected, and email' },
  { key: 'aht', label: 'AHT', type: 'duration', title: 'Average handle time: voice and callback time over voice handled (email for an email-only agent)' },
  { key: 'avgHold', label: 'Hold', type: 'duration', title: 'Average length of a hold' },
  { key: 'handledPerHour', label: 'Hand/Hr', type: 'rate', title: 'Handled per hour On Queue' },
  { key: 'transferred', label: 'Xfer', type: 'count', title: 'Transfers' },
  { key: 'xferPct', label: 'Xfer %', type: 'pct', title: 'Transfers over Handled' },
  { key: 'adherencePct', label: 'Adher', type: 'adherence', title: 'Schedule adherence over the period, weighted by scheduled time' },
  { key: 'efficiency', label: 'Eff%', type: 'efficiency', title: 'Working time over time logged in, less break and lunch' },
];

// QA and CSAT are one column each at the end, not one per period: the old app
// read them from the longer period and said so.
const TAIL_COLUMNS = [
  { key: 'qa', label: 'QA', type: 'qa', title: 'Average QA score of evaluations completed in the period, with how many' },
  { key: 'csat', label: 'CSAT', type: 'csat', title: 'Average survey score in the period, with how many responses' },
];

const TOGGLEABLE = [...PERIOD_COLUMNS, ...TAIL_COLUMNS].map((col) => col.label);
const STORAGE_KEY = 'agentTable.columns.v1';

function storedColumns() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(saved)) return new Set(saved);
  } catch { /* a browser that refuses storage still gets every column */ }
  return new Set(TOGGLEABLE);
}

// The gear. Only the eleven above can be turned off; Agent, Pod and the rank
// are what the row is, not a figure about it.
function ColumnPicker({ visible, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div ref={box} style={s.gearWrap}>
      <button type="button" onClick={() => setOpen((v) => !v)} title="Choose columns"
        aria-expanded={open} aria-haspopup="true" style={s.gear}>⚙</button>
      {open && (
        <div style={s.menu}>
          <div style={s.menuHead}>Columns</div>
          {TOGGLEABLE.map((label) => (
            <label key={label} style={s.menuRow}>
              <input type="checkbox" checked={visible.has(label)} onChange={() => onToggle(label)} style={s.check} />
              {label}
            </label>
          ))}
          <div style={s.menuFoot}>
            <button type="button" onClick={onReset} style={s.reset}>Reset to defaults</button>
          </div>
        </div>
      )}
    </div>
  );
}

// An agent with time under presence codes the portal cannot name yet has no
// Efficiency: say so, and how much, rather than show a blank.
function EfficiencyMissing({ secs }) {
  return (
    <span style={{ color: c.textFaint, fontSize: '11px' }}
      title={`${fmtHm(secs)} under presence codes Genesys does not yet show the portal; Efficiency needs them all`}>
      incomplete
    </span>
  );
}

function Avatar({ name }) {
  return <span style={{ ...s.avatar, background: avatarColor(name) }} aria-hidden="true">{initials(name)}</span>;
}

function PodBadge({ pod, fromQueue }) {
  if (!pod) return <span style={{ color: c.textFaint }}>Unknown</span>;
  const tone = podTone(pod);
  return (
    <span style={{ ...s.podBadge, background: tone.bg, color: tone.fg }}
      title={fromQueue ? 'On no pod team in Genesys: placed by the queue they handled most' : undefined}>
      <span style={{ ...s.podDot, background: tone.dot }} />
      {pod}{fromQueue && '*'}
    </span>
  );
}

function Channels({ channels }) {
  const list = Object.entries(channels || {}).filter(([, n]) => n > 0).sort((x, y) => y[1] - x[1]);
  if (!list.length) return <span style={{ color: c.textFaint }}>—</span>;
  const rest = list.length - 1;
  return (
    <span style={s.channelWrap}>
      <span style={s.channel}>
        <span style={{ ...s.channelDot, background: channelDot(list[0][0]) }} />
        {list[0][0]}
      </span>
      {rest > 0 && (
        <span style={s.more} title={list.map(([ch, n]) => `${ch}: ${n.toLocaleString('en-US')}`).join('\n')}>+{rest}</span>
      )}
    </span>
  );
}

/** Score over a count, the old app's two-line cell: the figure, then n=N. */
function ScoreCell({ value, count, format }) {
  if (!count || value == null) return <span style={{ color: c.textFaint }}>—</span>;
  const tone = scoreTone(value, count);
  return (
    <div style={s.twoLine}>
      <div style={{ fontWeight: 600, color: INK[tone] || c.textStrong }}>{format(value)}</div>
      <div style={s.n}>n={count}</div>
    </div>
  );
}

const valueOf = (a, key) => {
  if (!a) return null;
  if (key === 'xferPct') return transferPct(a);
  return a[key];
};

// Ranked within a pod by the leading period's Handled, whatever the sort; in
// a pod of more than six, the top three are tinted green and the bottom three
// amber (the old Supervisor page).
function ranksByPod(agents, lead) {
  const ranks = new Map();
  const byPod = new Map();
  for (const a of agents) {
    if (!byPod.has(a.pod)) byPod.set(a.pod, []);
    byPod.get(a.pod).push(a);
  }
  for (const list of byPod.values()) {
    const sorted = [...list].sort((x, y) => (lead.byUser.get(y.userId)?.handled || 0) - (lead.byUser.get(x.userId)?.handled || 0)
      || (x.name || '').localeCompare(y.name || ''));
    sorted.forEach((a, i) => ranks.set(a.userId, {
      rank: i + 1,
      tint: sorted.length > 6 ? (i < 3 ? 'top' : i >= sorted.length - 3 ? 'bottom' : null) : null,
    }));
  }
  return ranks;
}

// holdTotal: Hold is the period's total time on hold rather than the average
// hold. The old Director, Manager and Supervisor tables did that for a range
// (Yesterday, YTD, Custom) and averaged for Today and MTD; the old Agents page
// always averaged.
const TOTAL_HOLD = { key: 'holdSecs', label: 'Hold', type: 'duration', title: 'Total time on hold in the period' };

export default function AgentTable({ agents, periods, onOpen, selectedId, groupByPod = false, holdTotal = false }) {
  const lead = periods[0].key;
  const [sort, setSort] = useState({ period: lead, key: 'handled', dir: 'desc' });
  const [visible, setVisible] = useState(storedColumns);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...visible])); } catch { /* not worth failing over */ }
  }, [visible]);
  const toggleColumn = (label) => setVisible((cur) => {
    const next = new Set(cur);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });

  const cols = useMemo(() => PERIOD_COLUMNS.filter((col) => visible.has(col.label))
    .map((col) => (holdTotal && col.key === 'avgHold' ? TOTAL_HOLD : col)), [visible, holdTotal]);
  const tail = useMemo(() => TAIL_COLUMNS.filter((col) => visible.has(col.label)), [visible]);
  // QA and CSAT come from the longest period shown — MTD beside Today, as the
  // old app read them.
  const scorePeriod = periods[periods.length - 1];

  const active = periods.some((p) => p.key === sort.period) ? sort : { period: lead, key: 'handled', dir: 'desc' };
  const sortValue = (a) => {
    if (active.key === 'name') return (a.name || '').toLowerCase();
    if (active.key === 'pod') return a.pod || '';
    if (active.key === 'qa') return scorePeriod.byUser.get(a.userId)?.qaAvg ?? null;
    if (active.key === 'csat') return scorePeriod.byUser.get(a.userId)?.csatScore ?? null;
    if (active.key === 'channels') {
      const ch = periods.find((p) => p.key === active.period).byUser.get(a.userId)?.channels || {};
      return Object.values(ch).reduce((sum, n) => sum + n, 0) || null;
    }
    return valueOf(periods.find((p) => p.key === active.period).byUser.get(a.userId), active.key);
  };
  const rows = [...agents].sort((x, y) => {
    const vx = sortValue(x);
    const vy = sortValue(y);
    if (vx == null && vy == null) return (x.name || '').localeCompare(y.name || '');
    if (vx == null) return 1;                         // no figure sorts last, either direction
    if (vy == null) return -1;
    const cmp = typeof vx === 'string' ? vx.localeCompare(vy) : vx - vy;
    return (active.dir === 'asc' ? cmp : -cmp) || (x.name || '').localeCompare(y.name || '');
  });

  const ranks = groupByPod ? ranksByPod(agents, periods[0]) : null;
  // Grouped: pods in order of their agents' leading-period Handled, each
  // pod's agents in the chosen sort order beneath its header.
  let groups = [{ pod: null, rows }];
  if (groupByPod) {
    const total = (list) => list.reduce((sum, a) => sum + (periods[0].byUser.get(a.userId)?.handled || 0), 0);
    const map = new Map();
    for (const a of rows) {
      if (!map.has(a.pod)) map.set(a.pod, []);
      map.get(a.pod).push(a);
    }
    groups = [...map].map(([pod, list]) => ({ pod, rows: list, handled: total(list),
      active: list.filter((a) => (periods[0].byUser.get(a.userId)?.handled || 0) > 0).length }))
      .sort((x, y) => y.handled - x.handled || String(x.pod).localeCompare(String(y.pod)));
  }
  const width = 2 + (groupByPod ? 1 : 0) + periods.length * cols.length + tail.length;

  const sortBy = (period, key) => setSort((cur) => ({
    period, key,
    dir: cur.period === period && cur.key === key ? (cur.dir === 'desc' ? 'asc' : 'desc') : (key === 'name' || key === 'pod' ? 'asc' : 'desc'),
  }));
  const ariaSort = (period, key) => (active.period === period && active.key === key
    ? (active.dir === 'asc' ? 'ascending' : 'descending') : 'none');
  const arrow = (period, key) => (active.period === period && active.key === key ? (active.dir === 'asc' ? ' ▲' : ' ▼') : '');
  const header = (period, key, label, title, numeric) => (
    <th key={`${period}.${key}`} style={numeric ? s.thNum : s.th} aria-sort={ariaSort(period, key)} title={title}>
      <button type="button" style={{ ...s.sortBtn, textAlign: numeric ? 'right' : 'left' }} onClick={() => sortBy(period, key)}>
        {label}{arrow(period, key)}
      </button>
    </th>
  );

  // One period's cell for one agent, by the old app's rules for its type.
  const cell = (col, period, a) => {
    const f = period.byUser.get(a.userId);
    const key = `${period.key}.${col.key}`;
    if (col.type === 'channels') return <td key={key} style={s.td}><Channels channels={f?.channels} /></td>;
    if (col.type === 'adherence') {
      const v = f?.adherencePct ?? null;
      const tone = adherenceTone(v);
      return (
        <td key={key} style={s.num}
          title={f?.adherenceDays ? `${f.adherenceDays} scheduled day${f.adherenceDays === 1 ? '' : 's'}` : 'No WFM schedule in the period'}>
          {v == null ? <span style={{ color: c.textFaint }}>—</span>
            : <span style={{ ...s.chip, background: TONE[tone].bg, color: TONE[tone].fg }}>{fmtPct(v)}</span>}
        </td>
      );
    }
    if (col.type === 'efficiency') {
      const v = f?.efficiency ?? null;
      const tone = efficiencyTone(v);
      return (
        <td key={key} style={{ ...s.num, color: INK[tone] || c.textStrong, fontWeight: tone ? 600 : 400 }}>
          {v == null && f?.efficiencyUnnamedSecs > 0 ? <EfficiencyMissing secs={f.efficiencyUnnamedSecs} /> : fmtPct(v)}
        </td>
      );
    }
    if (col.type === 'count') return <td key={key} style={s.num}>{fmtCount(f ? valueOf(f, col.key) : null)}</td>;
    // Everything else is blank for a period the agent handled nothing in.
    const none = !f || !f.handled;
    const v = none ? null : valueOf(f, col.key);
    if (col.type === 'duration') {
      const stuck = col.key === 'aht' && f?.stuckExcludedSecs > 0;
      return (
        <td key={key} style={s.num} title={stuck ? `${fmtClock(f.stuckExcludedSecs)} of time on legs Genesys left open was left out` : undefined}>
          {fmtClock(v)}{stuck && <span style={s.flag} aria-label="adjusted"> ⚠</span>}
        </td>
      );
    }
    if (col.type === 'rate') return <td key={key} style={s.num}>{v == null ? '—' : v.toFixed(1)}</td>;
    return <td key={key} style={s.num}>{fmtPct(v)}</td>;
  };

  return (
    <section style={s.card} aria-label="Agents">
      <div style={s.toolbar}>
        <ColumnPicker visible={visible} onToggle={toggleColumn} onReset={() => setVisible(new Set(TOGGLEABLE))} />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            {periods.length > 1 && (
              <tr>
                <th colSpan={groupByPod ? 3 : 2} style={s.group} />
                {periods.map((p) => <th key={p.key} colSpan={cols.length} style={s.group}>{p.label}</th>)}
                {tail.length > 0 && <th colSpan={tail.length} style={s.group}>{scorePeriod.label}</th>}
              </tr>
            )}
            <tr>
              {groupByPod && <th style={s.thNum} title={`Rank in the pod by ${periods[0].label} Handled`}>#</th>}
              {header(lead, 'name', 'Agent', null, false)}
              {header(lead, 'pod', 'Pod', 'The pod whose queues the agent handled most in this window', false)}
              {periods.map((p) => cols.map((col) => header(p.key, col.key, col.label, col.title, col.align !== 'left')))}
              {tail.map((col) => header(scorePeriod.key, col.key, col.label, col.title, true))}
            </tr>
          </thead>
          <tbody>
            {groups.flatMap((g) => [
              ...(g.pod ? [(
                <tr key={`pod-${g.pod}`}>
                  <th colSpan={width} style={{ ...s.podHead, borderLeft: `3px solid ${podTone(g.pod).dot}` }} scope="colgroup">
                    {g.pod} · {g.rows.length} agent{g.rows.length === 1 ? '' : 's'} · {g.active} active {periods[0].label.toLowerCase()} · {fmtCount(g.handled)} handled
                  </th>
                </tr>
              )] : []),
              ...g.rows.map((a) => {
                const r = ranks?.get(a.userId);
                const tint = r?.tint === 'top' ? c.successTint : r?.tint === 'bottom' ? c.warningTint : undefined;
                const scores = scorePeriod.byUser.get(a.userId);
                return (
              <tr key={a.userId} onClick={() => onOpen(a.userId)}
                style={{ ...(a.userId === selectedId ? s.rowOn : s.row), ...(tint && a.userId !== selectedId ? { background: tint } : {}) }}>
                {groupByPod && <td style={s.num}>{r?.rank}</td>}
                <td style={s.td}>
                  <span style={s.nameCell}>
                    <Avatar name={a.name} />
                    <button type="button" style={s.nameBtn} onClick={(e) => { e.stopPropagation(); onOpen(a.userId); }}>
                      {a.name || 'Unknown agent'}
                    </button>
                    {a.state && a.state !== 'active' && <span style={s.left}>left</span>}
                    {a.queueMember === false && (
                      <span style={s.left} title="Not a member of this team's queues. The old app left such agents out; the work they handled is counted here.">
                        not in queue
                      </span>
                    )}
                  </span>
                </td>
                <td style={s.td}><PodBadge pod={a.pod} fromQueue={a.podSource === 'queue'} /></td>
                {periods.map((p) => cols.map((col) => cell(col, p, a)))}
                {tail.map((col) => (
                  <td key={col.key} style={s.num}>
                    {col.key === 'qa'
                      ? <ScoreCell value={scores?.qaAvg ?? null} count={scores?.qaCount || 0} format={(v) => `${v.toFixed(1)}%`} />
                      : <ScoreCell value={scores?.csatScore ?? null} count={scores?.csatCount || 0} format={(v) => `${v.toFixed(1)}%`} />}
                  </td>
                ))}
              </tr>
                );
              }),
            ])}
            {!rows.length && (
              <tr><td colSpan={width} style={s.empty}>No agents match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const s = {
  card:     { ...card.base, padding: '6px 0' },
  toolbar:  { display: 'flex', justifyContent: 'flex-end', padding: '2px 14px 6px' },
  table:    { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  group:    { ...tbl.thSm, textAlign: 'center', borderBottom: `1px solid ${c.border}`, color: c.textStrong },
  th:       { ...tbl.th, textAlign: 'left', whiteSpace: 'nowrap' },
  thNum:    { ...tbl.thR, whiteSpace: 'nowrap' },
  sortBtn:  { all: 'unset', cursor: 'pointer', display: 'block', width: '100%', font: 'inherit', color: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' },
  td:       { ...tbl.td, color: c.textStrong, whiteSpace: 'nowrap' },
  num:      { ...tbl.tdR, color: c.textStrong },
  row:      { ...tbl.trHover },
  rowOn:    { ...tbl.trHover, background: c.surfaceSunk },
  nameCell: { display: 'inline-flex', alignItems: 'center', gap: '8px' },
  nameBtn:  { all: 'unset', cursor: 'pointer', fontWeight: 550, color: c.text },
  avatar:   { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px',
    borderRadius: '999px', color: '#fff', fontSize: '10px', fontWeight: 700, flexShrink: 0 },
  podBadge: { display: 'inline-flex', alignItems: 'center', gap: '5px', borderRadius: '4px',
    padding: '1px 6px', fontSize: '10.5px', fontWeight: 600 },
  podDot:   { width: '6px', height: '6px', borderRadius: '2px', flexShrink: 0 },
  channelWrap: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  channel:  { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: c.textStrong },
  channelDot: { width: '6px', height: '6px', borderRadius: '999px', flexShrink: 0 },
  chip:     { display: 'inline-block', padding: '1px 6px', borderRadius: '4px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  twoLine:  { lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' },
  n:        { fontSize: '10px', color: c.textFaint },
  left:     { marginLeft: '2px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: c.textMuted, background: c.surfaceSunk, padding: '1px 6px', borderRadius: '999px' },
  more:     { fontSize: '10px', color: c.textMuted, background: c.surfaceSunk, padding: '1px 4px', borderRadius: '4px', cursor: 'help' },
  flag:     { color: c.warningText, fontSize: '10px' },
  empty:    { ...tbl.td, textAlign: 'center', color: c.textMuted, padding: '24px' },
  podHead:  { ...tbl.thSm, textAlign: 'left', background: c.surfaceSunk, color: c.textStrong, textTransform: 'none', letterSpacing: 0, fontSize: '12px' },
  gearWrap: { position: 'relative' },
  gear:     { all: 'unset', cursor: 'pointer', border: `1px solid ${c.border}`, borderRadius: '6px',
    padding: '2px 8px', fontSize: '13px', color: c.textMuted, background: c.surface },
  menu:     { position: 'absolute', right: 0, marginTop: '4px', width: '192px', zIndex: 40,
    background: c.surface, border: `1px solid ${c.border}`, borderRadius: '8px', boxShadow: '0 8px 24px rgba(15,23,42,0.12)', padding: '4px 0' },
  menuHead: { padding: '4px 12px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textMuted },
  menuRow:  { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px', fontSize: '12px', color: c.textStrong, cursor: 'pointer' },
  check:    { accentColor: c.brand, cursor: 'pointer' },
  menuFoot: { borderTop: `1px solid ${c.border}`, marginTop: '4px', padding: '6px 12px' },
  reset:    { all: 'unset', cursor: 'pointer', fontSize: '12px', color: c.brand },
};
